'use client';

import { useTranslations } from 'next-intl';
import { COMPANY } from '@/lib/company';

/**
 * Which sentence to draw. Both offer the shop's phone and email; they differ in
 * what they say around them.
 *
 * - `noResultsCallUs` — under a search that found nothing, so it leads with the
 *   retry advice before offering a person to talk to.
 * - `helpDescription` — the "Hittar du inte det du söker?" contact card, which
 *   is contact details first.
 */
export type ContactMessage = 'noResultsCallUs' | 'helpDescription';

interface SearchContactTextProps {
  messageKey: ContactMessage;
  className?: string;
}

/**
 * The shop's phone and email, rendered as links inside a translated sentence.
 *
 * One renderer for every place that offers a stuck customer a way to reach the
 * shop: the search results page's contact card, the desktop overlay's and the
 * mobile overlay's copies of it, and the no-results sentence in both overlays.
 *
 * It exists because those were four hand-rolled copies of the same rich-text
 * call, each declaring its own phone handler — and when Torsbo asked for an
 * email address alongside the phone (p.14 of the "Hemsidan 2.0" feedback),
 * adding it to some of them and not others is exactly what happened. A single
 * renderer means the next change lands everywhere at once.
 *
 * The numbers come from lib/company.ts rather than from the message, so the
 * link target and the visible text can never disagree.
 *
 * NOTE on naming: next-intl resolves value placeholders and tag handlers from
 * one namespace, so each pair needs two distinct names — hence
 * `phoneNumber`/`phone` and `emailAddress`/`email`.
 */
export function SearchContactText({ messageKey, className }: SearchContactTextProps) {
  const t = useTranslations('search');
  const tel = COMPANY.phone.replace(/[^\d+]/g, '');

  return (
    <p className={className}>
      {t.rich(messageKey, {
        phoneNumber: COMPANY.phone,
        emailAddress: COMPANY.email,
        phone: (chunks) => (
          <a href={`tel:${tel}`} className="font-semibold">
            {chunks}
          </a>
        ),
        email: (chunks) => (
          <a href={`mailto:${COMPANY.email}`} className="font-semibold break-all">
            {chunks}
          </a>
        ),
      })}
    </p>
  );
}
